import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * Incrémente le compteur de visites pour `pagePath` UNE fois au
 * mount du composant. Utilise un ref pour éviter le double-call
 * en mode strict React (qui double-monte les composants en dev).
 *
 * Aussi invalide la queryKey du compteur pour que useTotalVisits
 * refresh la valeur affichée après l'incrément.
 */
export function useIncrementPageVisit(pagePath: string) {
  const queryClient = useQueryClient();
  const hasIncrementedRef = useRef(false);

  useEffect(() => {
    if (hasIncrementedRef.current) return;
    hasIncrementedRef.current = true;

    (async () => {
      try {
        await supabase.rpc('increment_page_visit', { page: pagePath });
        // Invalide pour que le compteur affiché se met à jour
        queryClient.invalidateQueries({
          queryKey: ['page-visits', pagePath],
        });
      } catch (e) {
        // Silent fail : un compteur cassé ne doit pas casser la page
        if (import.meta.env.DEV) {
          console.warn('increment_page_visit failed:', e);
        }
      }
    })();
  }, [pagePath, queryClient]);
}

/**
 * Lit le compteur actuel de visites pour `pagePath`. Retourne 0
 * si la page n'a jamais été visitée.
 */
export function usePageVisitCount(pagePath: string) {
  return useQuery({
    queryKey: ['page-visits', pagePath],
    staleTime: 60 * 1000, // 1 min — pas besoin d'une fraîcheur extrême
    queryFn: async (): Promise<number> => {
      const { data, error } = await supabase
        .from('page_visits')
        .select('visit_count')
        .eq('page_path', pagePath)
        .maybeSingle();
      if (error) {
        if (import.meta.env.DEV) {
          console.warn('usePageVisitCount fallback:', error.message);
        }
        return 0;
      }
      return (data?.visit_count as number) ?? 0;
    },
  });
}
